import { Box, Button, Group, Skeleton, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router"
import useSWR from "swr"
import { OrderApi } from "../../apis"
import { checkoutErrorText, loadSuccessState, type CheckoutResult, type SuccessState } from "../../components/buyer-sales/product-list/cart-model"
import classes from "../../components/buyer-sales/product-list/CartDrawer.module.css"
import { DotPill, TableFrame, type Tone } from "../../components/delivery/atoms"
import { PageLayout } from "../../components/layout"
import { ListState } from "../../components/ui"
import { addToCart, updateQuantity, type ICart } from "../../store/Cart"
import { currencyFormat } from "../../utils/currency"
import { dateFormat } from "../../utils/date"
import { message } from "../../utils/message"
import { effectiveOrderStatus, orderStatusUi } from "../../utils/order-status-ui"

const STATUS_TONE: Record<string, Tone> = {
  DRAFT: "grey",
  REGISTERED: "blue",
  REVIEWED: "amber",
  APPROVED: "green",
  AUTHORIZED: "green",
  PREPARING: "blue",
  DELIVERING: "blue",
  DELIVERED: "green",
  CLOSED: "green",
  REVIEW_REJECTED: "red",
  CANCELED: "red",
  AUTO_CANCELED: "red",
  REJECTED: "red",
}

/**
 * Order sent / draft saved (canvas OrdB_Success, B10). One card per created
 * order, read back from GET /ord/order/:id; suppliers that failed are listed
 * with their reason (their lines stayed in the cart).
 */
export function BuyerSalesSuccess() {
  const { t } = useTranslation(["order", "common"])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const cart = useSelector((state: { cart: ICart }) => state.cart.items)
  const state = (location.state as SuccessState | null) ?? loadSuccessState()

  const done = state?.results.filter((r) => r.orderId) ?? []
  const failed = state?.results.filter((r) => !r.orderId) ?? []
  const title = state?.send === false ? t("order:cart.success.titleSaved") : t("order:cart.success.titleSent")
  const breadcrumb = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/buyer-sales", label: t("order:buyerSales.breadcrumb") },
    { label: title },
  ]

  const handleReorder = () => {
    state?.lines.forEach((line) => {
      const existing = cart.find((i) => i.id === line.id && i.supplierBusinessId === line.supplierBusinessId)
      if (existing) {
        dispatch(updateQuantity({ productId: line.id, supplierBusinessId: line.supplierBusinessId, quantity: existing.quantity + line.quantity }))
      } else {
        dispatch(addToCart(line))
      }
    })
    message.success(t("order:cart.success.reorderDone"))
    navigate("/buyer-sales/new", { state: { openCart: true } })
  }

  if (!state || done.length === 0) {
    return (
      <PageLayout title={title} breadcrumb={breadcrumb} noFrame>
        <TableFrame>
          <ListState
            state="empty"
            title={t("order:cart.success.noResultTitle")}
            description={t("order:cart.success.noResultText")}
            action={{ label: t("order:cart.success.viewOrders"), onClick: () => navigate("/buyer-sales") }}
          />
        </TableFrame>
      </PageLayout>
    )
  }

  return (
    <PageLayout title={title} breadcrumb={breadcrumb} noFrame>
      <Box className={classes.success}>
        {done.map((r) => (
          <OrderCard key={r.orderId} result={r} send={state.send} />
        ))}

        {failed.length > 0 && (
          <Box role="alert" className={`${classes.card} ${classes.cardPad}`}>
            <Text className={classes.cardTitle}>{t("order:cart.success.failedTitle", { count: failed.length })}</Text>
            {failed.map((f) => (
              <Text key={f.supplierId} className={classes.sub} mt={6}>
                {f.supplierName}: {checkoutErrorText(t, f.error, cart)}
              </Text>
            ))}
            <Text className={classes.sub} mt="sm">
              {t("order:cart.success.failedHint")}
            </Text>
          </Box>
        )}

        <Group gap="sm">
          <Button
            size="md"
            onClick={() => navigate(done.length === 1 ? `/buyer-sales/detail/${done[0].orderId}` : "/buyer-sales")}
          >
            {t("order:cart.success.viewOrders")}
          </Button>
          <Button size="md" variant="default" onClick={handleReorder}>
            {t("order:cart.success.reorder")}
          </Button>
          <Button size="md" variant="default" onClick={() => navigate("/buyer-sales/new")}>
            {t("order:cart.success.backToCatalog")}
          </Button>
        </Group>
      </Box>
    </PageLayout>
  )
}

function OrderCard({ result, send }: { result: CheckoutResult; send: boolean }) {
  const { t } = useTranslation(["order", "common"])
  const { data: order, error, isLoading } = useSWR(result.orderId ? ["checkout.success.order", result.orderId] : null, () =>
    OrderApi.get(result.orderId!),
    { revalidateOnFocus: false },
  )

  const status = effectiveOrderStatus(order)
  const ui = orderStatusUi(status)
  const lines: any[] = order?.lines ?? []
  const pieces = lines.reduce((sum, l) => sum + (Number(l?.quantity) || 0), 0)

  return (
    <Box component="section" className={`${classes.card} ${classes.cardPad}`} aria-label={order?.purchaseCode || result.supplierName}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box>
          <Text className={classes.sub}>{t("order:cart.success.orderNo")}</Text>
          {isLoading ? <Skeleton h={28} w={160} mt={4} /> : <Text className={classes.orderNo}>{order?.purchaseCode || "-"}</Text>}
        </Box>
        {order && <DotPill tone={STATUS_TONE[status] ?? "grey"}>{ui.label}</DotPill>}
      </Group>

      {error ? (
        <Text className={classes.sub} mt="md">
          {t("order:cart.success.loadFailed")}
        </Text>
      ) : (
        <Box className={classes.facts}>
          <Text span className={classes.factLabel}>{t("order:cart.success.supplier")}</Text>
          <Text span className={classes.factValue} fw={600}>
            {order?.receiverBusiness?.profileName || result.supplierName || "-"}
          </Text>
          <Text span className={classes.factLabel}>{t("order:cart.success.deliveryDate")}</Text>
          <Text span className={`${classes.factValue} ${classes.mono}`}>{dateFormat(order?.deliveryDate)}</Text>
          <Text span className={classes.factLabel}>{t("order:cart.success.paymentTerm")}</Text>
          <Text span className={classes.factValue}>{order?.paymentTerm?.description || "-"}</Text>
          <Text span className={classes.factLabel}>{t("order:cart.success.items")}</Text>
          <Text span className={classes.factValue}>
            {order ? t("order:cart.success.itemsValue", { types: lines.length, count: pieces }) : "-"}
          </Text>
          <Text span className={classes.factLabel}>{t("order:cart.success.total")}</Text>
          <Text span className={classes.amount}>{order ? currencyFormat(Number(order.totalAmount) || 0) : "-"}</Text>
        </Box>
      )}

      <Text className={classes.next}>{send ? t("order:cart.success.nextSent") : t("order:cart.success.nextSaved")}</Text>
    </Box>
  )
}
